import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton, Button, Card, Avatar } from 'antd';
import { PlusOutlined, TeamOutlined, TrophyOutlined, CopyOutlined } from '@ant-design/icons';
import { Campaign, Enrollment } from 'models/CommonType';
import { useTeams } from 'services/TeamService';
import TeamRow from './TeamRow';
import TeamCreationModal from './TeamCreationModal';
import TeamListModal from './TeamListModal';
import './Teams.css';

interface TeamsProps {
  campaign: Campaign;
  authKey: string;
  hasTeam: boolean;
  isJoined: boolean;
  enrollment?: Enrollment;
}

const Teams: React.FC<TeamsProps> = ({ 
  campaign, 
  authKey, 
  hasTeam,
  isJoined,
  enrollment
}) => {
  const { t } = useTranslation();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isTeamListModalOpen, setIsTeamListModalOpen] = useState(false);
  
  const { data: teams, isLoading, mutate: refetchTeams } = useTeams(
    campaign.id,
    authKey,
    '', // searchText
    1,  // pageNumber
    isJoined // only fetch if user is joined
  );

  if (!isJoined) {
    return null;
  }

  const handleCreateTeamSuccess = () => {
    refetchTeams(); // Refresh teams list
    setIsCreateModalOpen(false);
  };

  const renderEmptyState = () => (
    <Card className="teams-empty-card">
      <div className="teams-empty-content">
        <div className="empty-icon">
          <TeamOutlined />
        </div>
        <h3 className="empty-title">{t('#Teams.empty_team_message')}</h3>
        <p className="empty-description">
          Create a team to collaborate with others and compete together!
        </p>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsCreateModalOpen(true)}
          className="create-team-primary-button"
          size="large"
        >
          {t('#Teams.create_team_link')}
        </Button>
      </div>
    </Card>
  );

  const renderTeamOverview = () => {
    if (!teams || teams.length === 0) {
      return renderEmptyState();
    }

    const userTeam = teams.find(team => 
      team.members.some(member => member.id === enrollment?.user?.id)
    );

    return (
      <div className="teams-overview">

        {/* User Team Section */}
        {userTeam && (
          <Card className="user-team-card">
            <div className="user-team-header">
              <div className="user-team-badge">
                <TrophyOutlined />
                <span>{t('#Teams.your_team')}</span>
              </div>
              <Button
                type="link"
                onClick={() => setIsTeamListModalOpen(true)}
                className="view-all-teams-link"
              >
                {t('#Teams.view_all_team')}
              </Button>
            </div>
            <div className="user-team-content">
              <TeamRow
                team={userTeam}
                hasTeam={hasTeam}
                shouldShowMiles={true}
              />
              
              {/* Team Join Code and QR Code Section - Compact Style */}
              <div className="team-join-section-compact">
                <h3 className="join-section-title">{t('#Teams.invite_friends')}</h3>
                <div className="join-code-qr-container">
                  <div className="qr-code-small">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(userTeam.code || '')}&format=png&margin=10`}
                      alt={t('#Teams.qr_code_title')}
                      className="qr-image-small"
                    />
                  </div>
                  <div className="join-code-info">
                    <div className="code-label">{t('#Teams.invite_code')}</div>
                    <div className="code-display-container">
                      <span className="code-value">{userTeam.code || ''}</span>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          navigator.clipboard.writeText(userTeam.code || '');
                        }}
                        title={t('#Teams.copy_join_code')}
                        size="small"
                        className="copy-button-small"
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="team-quick-stats">
                <div className="quick-stat">
                  <span className="stat-label">Members</span>
                  <span className="stat-value">{userTeam.members.length}</span>
                </div>
                <div className="quick-stat">
                  <span className="stat-label">Miles</span>
                  <span className="stat-value">{userTeam.miles}</span>
                </div>
              </div>
            </div>
          </Card>
        )}

        {/* Team Actions - Show only for users without teams and hide on mobile to avoid duplicates */}
        {!hasTeam && (
          <div className="teams-actions">
            <div className="no-team-actions">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setIsCreateModalOpen(true)}
                size="large"
                className="create-team-action-button"
              >
                {t('#Teams.create_team_link')}
              </Button>
              <Button
                type="default"
                icon={<TeamOutlined />}
                onClick={() => setIsTeamListModalOpen(true)}
                size="large"
                className="join-team-action-button"
              >
                {t('#Teams.view_all_team')}
              </Button>
            </div>
          </div>
        )}

        {/* Top Teams Preview */}
        {teams.length > 1 && (
          <Card className="top-teams-card">
            <div className="top-teams-header">
              <h3>Top Teams</h3>
              <Button
                type="link"
                onClick={() => setIsTeamListModalOpen(true)}
                className="view-all-link"
              >
                View All
              </Button>
            </div>
            <div className="top-teams-list">
              {teams.slice(0, 3).map((team, index) => (
                <div key={team.id} className="top-team-item">
                  <Avatar.Group size="small" maxCount={3}>
                    {team.members.map(member => (
                      <Avatar
                        key={member.id}
                        src={member.profile?.image_small_url}
                        size="small"
                      >
                        {member.first_name?.[0]}{member.last_name?.[0]}
                      </Avatar>
                    ))}
                  </Avatar.Group>
                  <div className="team-info">
                    <div className="team-name">{team.name}</div>
                    <div className="team-miles">{team.miles} miles</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    );
  };

  const renderLoadingState = () => (
    <div className="teams-loading">
      <Card className="loading-card">
        <div className="loading-header">
          <Skeleton.Input style={{ width: 200, height: 32 }} active />
          <Skeleton.Input style={{ width: 100, height: 16 }} active />
        </div>
        <div className="loading-content">
          <Skeleton.Avatar size={64} active />
          <div className="loading-text">
            <Skeleton.Input style={{ width: 150, height: 20 }} active />
            <Skeleton.Input style={{ width: 100, height: 16 }} active />
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="teams-container">
      {isLoading ? renderLoadingState() : renderTeamOverview()}

      {/* Team Creation Modal */}
      <TeamCreationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        campaign={campaign}
        authKey={authKey}
        onSuccess={handleCreateTeamSuccess}
      />

      {/* Team List Modal */}
      <TeamListModal
        isOpen={isTeamListModalOpen}
        onClose={() => {
          setIsTeamListModalOpen(false);
          // Refresh teams data when modal closes to show updated information
          refetchTeams();
        }}
        campaign={campaign}
        authKey={authKey}
        enrollment={enrollment}
      />
    </div>
  );
};

export default Teams;